import {useMemo, useState} from "react";
import {ContainerStatusEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseContainerStatusDataTableFilterProps = Omit<UseDataTableFilterProps<ContainerStatusEntity>, 'filterConfig'>

export function useContainerStatusDataTableFilter(props: UseContainerStatusDataTableFilterProps): UseDataTableFilterResult<ContainerStatusEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [routeQuery, setRouteQuery] = useState<string | undefined>(undefined);
  const [routeDebouncedQuery] = useDebouncedValue(routeQuery, 200);

  const [siteQuery, setSiteQuery] = useState<string | undefined>(undefined);
  const [siteDebouncedQuery] = useDebouncedValue(siteQuery, 200);

  const filterConfig: DataTableFilterConfig<ContainerStatusEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.containerName.toLowerCase().includes(query.toLowerCase())
      },
      "route": {
        setQuery: setRouteQuery,
        query: routeQuery,
        debouncedQuery: routeDebouncedQuery,
        filter: ({siteRouteName}, query) => !siteRouteName || !siteRouteName.toLowerCase().includes(query.toLowerCase())
      },
      "site": {
        setQuery: setSiteQuery,
        query: siteQuery,
        debouncedQuery: siteDebouncedQuery,
        filter: ({siteName}, query) => !siteName || !siteName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    routeQuery,
    routeDebouncedQuery,
    siteQuery,
    siteDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
